
import { AxiosResponse } from "axios";
import { API_URL } from "../constants/url-config";
import { AppraisalModel } from "../models/appraisal.model";
import { ControlFailureModel } from "../models/control-failure.model";
import { ScenarioMitigationModel } from "../models/scenario-mitigation.model";
import { ScenarioModel } from "../models/scenario.model";
import { getAsync, postAsync, patchAsync, putAsync, deleteAsync } from "../utils/http-client";



const serviceUrl = API_URL.ermService;

const API_PATH = {
    lostEvents: "/loss-events",
    lossScenarios: "/loss-scenarios",
    mitigation: "/scenario-mitigation",
    mitigations: "/scenario-mitigations",
    controlEffectivenesses: "/control-effectivenesses",
    appraisal: "/appraisal-tool",
    benefitTypes: "/breakdown-funding-and-benefit-types",
    ermReport: "/erm/report",
    reports: "/reports",
    comments: "/comments",
    approvers: "/approvers",
    scenarioInvite: "/loss-scenarios/invite"
}

/************************************* Lost Event ************************************/
const getLostEventsAsync = async (): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, API_PATH.lostEvents, undefined, false);
}

const getLostEventAsyns = async (id: number): Promise<AxiosResponse<ControlFailureModel>> => {
    return await getAsync(serviceUrl, `${API_PATH.lostEvents}/${id}`);
}

const newLostEventAsync = async (model: ControlFailureModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, API_PATH.lostEvents, model, "", false);
}

const saveDraftLostEventAsync = async (id: number | undefined, model: ControlFailureModel, showLoading: boolean): Promise<AxiosResponse> => {
    return await patchAsync(serviceUrl, `${API_PATH.lostEvents}/${id}`, model, "", showLoading, false);
}

const updateLostEventAsync = async (id: number, model: ControlFailureModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, `${API_PATH.lostEvents}/${id}`, model);
}

const getLostEventCommentsAsync = async (id: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.lostEvents}/${id}/${API_PATH.comments}`);
}

const createLostEventCommentAsync = async (id: number, message: string): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, `${API_PATH.lostEvents}/${id}/${API_PATH.comments}`, { message }, undefined, false);
}

/************************************* End Lost Event ************************************/


/************************************* Lost Scenario ************************************/

const scenarioInviteAsync = async (emails: string[]): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, API_PATH.scenarioInvite, { emails });
}

const getLossScenariosAsync = async (): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, API_PATH.lossScenarios, undefined, false);
}

const getLostScenarioAsyns = async (id: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.lossScenarios}/${id}`);
}

const newLostScenarioAsync = async (model: ScenarioModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, API_PATH.lossScenarios, model, undefined, false);
}

const saveDraftLostScenarioAsync = async (id: number, model: ScenarioModel, showLoading: boolean): Promise<AxiosResponse> => {
    return await patchAsync(serviceUrl, `${API_PATH.lossScenarios}/${id}`, model, "", showLoading, false);
}

const updateLostScenarioAsync = async (id: number, model: ScenarioModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, `${API_PATH.lossScenarios}/${id}`, model);
}

const getScenarioCommentsAsync = async (id: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.lossScenarios}/${id}/${API_PATH.comments}`);
}

const createScenarioCommentAsync = async (id: number, message: string): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, `${API_PATH.lossScenarios}/${id}/${API_PATH.comments}`, { message }, undefined, false);
}

/************************************* End Lost Scenario ************************************/

/************************************* Scenario Mitigation************************************/

const getMitigationAsyns = async (scenarioId: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.mitigation}`);
}

const newMitigationAsync = async (scenarioId: number): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.mitigation}`, {});
}

const saveDraftMitigationAsync = async (scenarioId: number, model: ScenarioMitigationModel, showLoading?: boolean): Promise<AxiosResponse> => {
    return await patchAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.mitigation}`, model, "", showLoading, false);
}

const updateMitigationAsync = async (scenarioId: number, model: ScenarioModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.mitigation}`, model);
}

const updateControlEffectivenessesAsync = async (mitigationId: number, model: ScenarioMitigationModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, `${API_PATH.mitigations}/${mitigationId}${API_PATH.controlEffectivenesses}/${model.id}`, model);
}

const deleteControlEffectivenessesAsync = async (mitigationId: number, id: number): Promise<AxiosResponse> => {
    return await deleteAsync(serviceUrl, `${API_PATH.mitigations}/${mitigationId}${API_PATH.controlEffectivenesses}/${id}`);
}

const createControlEffectivenessesAsync = async (mitigationId: number, model: ScenarioMitigationModel): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, `${API_PATH.mitigations}/${mitigationId}${API_PATH.controlEffectivenesses}`, model);
}

const getMitigationCommentsAsync = async (scenarioId: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.mitigation}/${API_PATH.comments}`);
}

const createMitigationCommentAsync = async (scenarioId: number, message: string): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.mitigation}/${API_PATH.comments}`, { message }, undefined, false);
}

/************************************* End Scenario Mitigation ************************************/

/************************************* Appraisal ************************************/
const getAppraisalAsyns = async (scenarioId: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.appraisal}`);
}

const newAppraisalAsync = async (scenarioId: number): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.appraisal}`, {});
}

const saveDraftAppraisalAsync = async (scenarioId: number, model: AppraisalModel, showLoading?: boolean): Promise<AxiosResponse> => {
    return await patchAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.appraisal}`, model, "", showLoading, false);
}

const updateAppraisalAsync = async (scenarioId: number, model: AppraisalModel): Promise<AxiosResponse> => {
    return await putAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.appraisal}`, model);
}

const getAppraisalCommentsAsync = async (scenarioId: number): Promise<AxiosResponse> => {
    return await getAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.appraisal}/${API_PATH.comments}`);
}

const createAppraisalCommentAsync = async (scenarioId: number, message: string): Promise<AxiosResponse> => {
    return await postAsync(serviceUrl, `${API_PATH.lossScenarios}/${scenarioId}${API_PATH.appraisal}/${API_PATH.comments}`, { message }, undefined, false);
}
/************************************* End Appraisal ************************************/

const getApprovers = async () => {
    return await getAsync(serviceUrl, API_PATH.approvers);
}

export const ERMService = {
    getLostEventsAsync,
    newLostEventAsync,
    saveDraftLostEventAsync,
    updateLostEventAsync,
    getLostEventAsyns,
    getLossScenariosAsync,
    getLostScenarioAsyns,
    newLostScenarioAsync,
    saveDraftLostScenarioAsync,
    updateLostScenarioAsync,
    getMitigationAsyns,
    newMitigationAsync,
    saveDraftMitigationAsync,
    updateMitigationAsync,
    updateControlEffectivenessesAsync,
    deleteControlEffectivenessesAsync,
    createControlEffectivenessesAsync,
    getAppraisalAsyns,
    newAppraisalAsync,
    saveDraftAppraisalAsync,
    updateAppraisalAsync,
    getLostEventCommentsAsync,
    createLostEventCommentAsync,
    createScenarioCommentAsync,
    getScenarioCommentsAsync,
    getMitigationCommentsAsync,
    createMitigationCommentAsync,
    getAppraisalCommentsAsync,
    createAppraisalCommentAsync,
    getApprovers,
    scenarioInviteAsync
};
